/**
 * Intent Code & QR Management Panel — Admin Dashboard
 *
 * View, create, and manage intent codes. Inline QR previews,
 * copy short/full URLs, download print-quality SVG, deactivate codes.
 *
 * API Endpoints:
 * - GET /api/v1/admin/intent-codes     — List with filters
 * - PATCH /api/v1/admin/intent-codes/:id — Update (deactivate, label, etc.)
 * - POST /api/v1/admin/intent-codes    — Create new code
 *
 * @story SCRUM-1471 — 0.1.1.10-03: Intent Code & QR Management Panel
 * @story SCRUM-1611 — Intent Code form: market dropdown, event selector, UTM fields
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import QRCode from 'qrcode';
import apiRequest from '../../services/api';
import QuestionResponseIntentDialog from '../../components/admin/QuestionResponseIntentDialog';
import ActionIntentDialog from '../../components/admin/ActionIntentDialog';

const INTENT_TYPE_BADGES = {
  question_response: { label: 'Question / Response', bg: '#f59e0b' },
  signup_request: { label: 'Signup', bg: '#8b5cf6' },
  rsvp: { label: 'RSVP', bg: '#3b82f6' },
  checkin: { label: 'Check-in', bg: '#10b981' },
  walk_in: { label: 'Walk In', bg: '#f97316' },
  event_rsvp: { label: 'Event RSVP', bg: '#009263' },
  event_checkin: { label: 'Event Check-in', bg: '#059669' },
  post_event_followup: { label: 'Post-Event Followup', bg: '#6366f1' },
  calendar_booking: { label: 'Calendar Booking', bg: '#0891b2' },
};

const VALID_INTENT_TYPES = ['question_response', 'signup_request', 'rsvp', 'checkin', 'walk_in', 'event_rsvp', 'event_checkin', 'post_event_followup', 'calendar_booking'];

// ─── QR SVG Component ──────────────────────────────────────────────

function QRPreview({ url, size = 80 }) {
  const [svgDataUrl, setSvgDataUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: size, margin: 2, errorCorrectionLevel: 'M' })
      .then((dataUrl) => {
        if (!cancelled) {
          setSvgDataUrl(dataUrl);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url, size]);

  if (!svgDataUrl) {
    return <div style={{ width: size, height: size, backgroundColor: '#f3f4f6', borderRadius: '4px' }} />;
  }

  return <img src={svgDataUrl} alt="QR Code" style={{ width: size, height: size, borderRadius: '4px' }} />;
}

// ─── Download QR SVG ────────────────────────────────────────────────

async function downloadQRSvg(code, label, shortUrl) {
  try {
    const svgString = await QRCode.toString(shortUrl, { type: 'svg', width: 300, margin: 4, errorCorrectionLevel: 'M' });
    const safeName = (label || code).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const filename = `qr-${safeName}-${code.slice(0, 4)}.svg`;
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    // Silently fail — toast handled at caller level if needed
  }
}

// ─── Copy to Clipboard ─────────────────────────────────────────────

async function copyToClipboard(text, setToast) {
  try {
    await navigator.clipboard.writeText(text);
    setToast('Copied to clipboard');
    setTimeout(() => setToast(null), 2000);
  } catch {
    setToast('Copy failed');
    setTimeout(() => setToast(null), 3000);
  }
}

// ─── Confirm Dialog ─────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
        maxWidth: '420px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>Confirm</h3>
        <p style={{ margin: '0 0 20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.5' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '8px 16px', borderRadius: '6px', border: 'none',
            backgroundColor: '#ef4444', color: '#fff', cursor: 'pointer',
            fontSize: '14px', fontWeight: 600,
          }}>Deactivate</button>
        </div>
      </div>
    </div>
  );
}

// ─── New Code Modal ─────────────────────────────────────────────────

function NewCodeModal({ eventId: propEventId, onSubmit, onCancel }) {
  const [intentType, setIntentType] = useState('rsvp');
  const [label, setLabel] = useState('');
  const [marketId, setMarketId] = useState('');
  const [eventId, setEventId] = useState(propEventId || '');
  const [expiresAt, setExpiresAt] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch markets for dropdown
  const [markets, setMarkets] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    apiRequest('/api/v1/admin/markets')
      .then((res) => { if (res.success) { setMarkets(res.data); } })
      .catch(() => {});
  }, []);

  // Fetch events filtered by selected market
  useEffect(() => {
    if (!marketId) {
      setEvents([]);
      return;
    }
    setLoadingEvents(true);
    apiRequest('/api/v1/admin/events')
      .then((res) => {
        if (res.success) {
          setEvents(res.data.filter((e) => e.market_id === marketId));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingEvents(false));
  }, [marketId]);

  const handleMarketChange = (e) => {
    setMarketId(e.target.value);
    if (!propEventId) {
      setEventId('');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        intent_type: intentType,
        market_id: marketId,
        event_id: eventId || undefined,
        label: label || undefined,
        expires_at: expiresAt || undefined,
        max_uses: maxUses ? parseInt(maxUses, 10) : undefined,
        utm_source: utmSource || undefined,
        utm_medium: utmMedium || undefined,
        utm_campaign: utmCampaign || undefined,
        utm_content: utmContent || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%' };
  const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
        maxWidth: '480px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>New Intent Code</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Intent Type *</label>
            <select value={intentType} onChange={(e) => setIntentType(e.target.value)} style={inputStyle}>
              {VALID_INTENT_TYPES.map((t) => (
                <option key={t} value={t}>{INTENT_TYPE_BADGES[t]?.label || t}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Market *</label>
            <select value={marketId} onChange={handleMarketChange} style={inputStyle}>
              <option value="">Select market...</option>
              {markets.map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({m.slug})</option>
              ))}
            </select>
          </div>

          {marketId && (
            <div>
              <label style={labelStyle}>Event {loadingEvents ? '(loading...)' : (intentType === 'event_checkin' || intentType === 'event_rsvp') ? '(required)' : '(optional)'}</label>
              <select value={eventId} onChange={(e) => setEventId(e.target.value)} style={inputStyle}
                disabled={loadingEvents}>
                <option value="">No event</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name} ({new Date(e.starts_at).toLocaleDateString()})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={labelStyle}>Label</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Front Door QR" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Expires At</label>
            <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
              style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Max Uses</label>
            <input type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited" style={inputStyle} />
          </div>

          <details style={{ marginTop: '4px' }}>
            <summary style={{ fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
              UTM Attribution (optional)
            </summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
              <input type="text" value={utmSource} onChange={(e) => setUtmSource(e.target.value)}
                placeholder="utm_source (e.g., qr_code)" style={inputStyle} />
              <input type="text" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)}
                placeholder="utm_medium (e.g., event)" style={inputStyle} />
              <input type="text" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)}
                placeholder="utm_campaign (e.g., colorado_2026)" style={inputStyle} />
              <input type="text" value={utmContent} onChange={(e) => setUtmContent(e.target.value)}
                placeholder="utm_content (e.g., front_door)" style={inputStyle} />
            </div>
          </details>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
          }}>Cancel</button>
          <button
            disabled={submitting || !marketId || !intentType || ((intentType === 'event_checkin' || intentType === 'event_rsvp') && !eventId)}
            onClick={handleSubmit}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#3b82f6', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600, opacity: (submitting || !marketId || ((intentType === 'event_checkin' || intentType === 'event_rsvp') && !eventId)) ? 0.6 : 1,
            }}
          >{submitting ? 'Creating...' : 'Create Code'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Code Row ───────────────────────────────────────────────────────

function CodeRow({ code, onDeactivate, onToast }) {
  const badge = INTENT_TYPE_BADGES[code.intent_type] || { label: code.intent_type, bg: '#6b7280' };
  const maskedCode = code.code.slice(0, 4) + '****';
  const shortUrl = code.short_url || `https://odg1.pro/${code.code}`;
  const fullUrl = `${window.location.origin}/i/${code.code}`;
  const emailUrl = `https://outdoorguides.com/i/${code.code}?cid={{contact.hs_object_id}}`;

  const btnStyle = {
    padding: '3px 8px', fontSize: '11px', borderRadius: '4px',
    border: '1px solid #d1d5db', backgroundColor: '#fff', cursor: 'pointer',
  };

  return (
    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
      <td style={{ padding: '8px' }}>
        <QRPreview url={shortUrl} size={60} />
      </td>
      <td style={{ padding: '8px' }}>
        <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '14px' }}>{maskedCode}</div>
        {code.label && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{code.label}</div>}
      </td>
      <td style={{ padding: '8px' }}>
        <span style={{
          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
          backgroundColor: badge.bg, color: '#fff', whiteSpace: 'nowrap',
        }}>{badge.label}</span>
      </td>
      <td style={{ padding: '8px', fontSize: '13px' }}>{code.use_count}{code.max_uses ? ` / ${code.max_uses}` : ''}</td>
      <td style={{ padding: '8px', fontSize: '12px', color: '#6b7280' }}>
        {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : 'Never'}
      </td>
      <td style={{ padding: '8px' }}>
        <span style={{
          padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
          backgroundColor: code.active ? '#d1fae5' : '#fee2e2',
          color: code.active ? '#065f46' : '#dc2626',
        }}>{code.active ? 'Active' : 'Inactive'}</span>
      </td>
      <td style={{ padding: '8px' }}>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <button onClick={() => copyToClipboard(shortUrl, onToast)} style={btnStyle} title="Copy short URL">
            Short URL
          </button>
          <button onClick={() => copyToClipboard(fullUrl, onToast)} style={btnStyle} title="Copy full URL">
            Full URL
          </button>
          <button onClick={() => downloadQRSvg(code.code, code.label || code.code, shortUrl)} style={btnStyle} title="Download print QR">
            Download QR
          </button>
          {code.intent_type === 'event_rsvp' && (
            <button onClick={() => copyToClipboard(emailUrl, onToast)} style={{ ...btnStyle, border: '1px solid #009263', color: '#009263' }} title="Copy HubSpot email URL">
              Email URL
            </button>
          )}
          {code.active && (
            <button
              onClick={() => onDeactivate(code)}
              style={{ ...btnStyle, border: '1px solid #ef4444', color: '#ef4444' }}
            >Deactivate</button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

export default function IntentCodePanel({ eventId: propEventId } = {}) {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [filterEventId, setFilterEventId] = useState(propEventId || '');
  const [filterActive, setFilterActive] = useState('');

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // When embedded inside EventDetail (propEventId set), skip DashboardLayout wrapper
  const isEmbedded = !!propEventId;

  const fetchCodes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterEventId) {
        params.set('event_id', filterEventId);
      }
      if (filterActive !== '') {
        params.set('active', filterActive);
      }

      const response = await apiRequest(`/api/v1/admin/intent-codes?${params.toString()}`);
      if (response.success) {
        setCodes(response.data);
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load intent codes');
    } finally {
      setLoading(false);
    }
  }, [filterEventId, filterActive]);

  useEffect(() => {
    setLoading(true);
    fetchCodes();
  }, [fetchCodes]);

  const handleDeactivate = async () => {
    if (!confirmDeactivate) {
      return;
    }
    try {
      await apiRequest(`/api/v1/admin/intent-codes/${confirmDeactivate.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: false }),
        headers: { 'Content-Type': 'application/json' },
      });
      setToast(`Code ${confirmDeactivate.code.slice(0, 4)}**** deactivated`);
      setTimeout(() => setToast(null), 3000);
      setConfirmDeactivate(null);
      await fetchCodes();
    } catch (err) {
      setToast(`Deactivate failed: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
      setConfirmDeactivate(null);
    }
  };

  const [showActionDialog, setShowActionDialog] = useState(null);

  const handleCreate = async (fields) => {
    if (fields.intent_type === 'question_response') {
      setShowNewModal(false);
      setShowQRDialog(true);
      return;
    }
    if (fields.intent_type === 'rsvp' || fields.intent_type === 'checkin' || fields.intent_type === 'walk_in' || fields.intent_type === 'signup_request' || fields.intent_type === 'event_rsvp') {
      setShowNewModal(false);
      setShowActionDialog(fields.intent_type);
      return;
    }
    try {
      await apiRequest('/api/v1/admin/intent-codes', {
        method: 'POST',
        body: JSON.stringify(fields),
        headers: { 'Content-Type': 'application/json' },
      });
      setToast('New intent code created');
      setTimeout(() => setToast(null), 3000);
      setShowNewModal(false);
      await fetchCodes();
    } catch (err) {
      setToast(`Create failed: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
      throw err;
    }
  };

  const handleCreateQuestionResponse = async (fields) => {
    try {
      const result = await apiRequest('/api/v1/admin/intent-codes/question-response', {
        method: 'POST',
        body: JSON.stringify(fields),
        headers: { 'Content-Type': 'application/json' },
      });
      setToast('Question response intent created');
      setTimeout(() => setToast(null), 3000);
      await fetchCodes();
      return result;
    } catch (err) {
      setToast(`Create failed: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
      throw err;
    }
  };

  const handleCreateActionIntent = async (fields) => {
    try {
      const result = await apiRequest('/api/v1/admin/intent-codes/action-intent', {
        method: 'POST',
        body: JSON.stringify({ ...fields, market_id: filterEventId ? undefined : fields.market_id }),
        headers: { 'Content-Type': 'application/json' },
      });
      const typeLabelMap = { rsvp: 'RSVP', checkin: 'Check-in', walk_in: 'Walk-in', signup_request: 'Signup Request', event_rsvp: 'Event RSVP' };
      setToast(`${typeLabelMap[fields.intent_type] || 'Action'} intent created`);
      setTimeout(() => setToast(null), 3000);
      await fetchCodes();
      return result;
    } catch (err) {
      setToast(`Create failed: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
      throw err;
    }
  };

  const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' };

  const content = (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Intent Code Management</h1>
        <button
          onClick={() => setShowNewModal(true)}
          style={{
            padding: '8px 16px', borderRadius: '6px', border: 'none',
            backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
            fontSize: '14px', fontWeight: 600,
          }}
        >New Code</button>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toast.includes('failed') ? '#fee2e2' : '#d1fae5',
          color: toast.includes('failed') ? '#dc2626' : '#065f46',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, fontSize: '14px',
        }}>{toast}</div>
      )}

      {confirmDeactivate && (
        <ConfirmDialog
          message={`Deactivate code ${confirmDeactivate.code.slice(0, 4)}****? This code will no longer be scannable.`}
          onConfirm={handleDeactivate}
          onCancel={() => setConfirmDeactivate(null)}
        />
      )}

      {showNewModal && (
        <NewCodeModal
          eventId={filterEventId}
          onSubmit={handleCreate}
          onCancel={() => setShowNewModal(false)}
        />
      )}

      {showQRDialog && (
        <QuestionResponseIntentDialog
          onSubmit={handleCreateQuestionResponse}
          onCancel={() => setShowQRDialog(false)}
          onToast={setToast}
        />
      )}

      {showActionDialog && (
        <ActionIntentDialog
          intentType={showActionDialog}
          onSubmit={handleCreateActionIntent}
          onCancel={() => setShowActionDialog(null)}
          onToast={setToast}
        />
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Event ID"
          value={filterEventId}
          onChange={(e) => setFilterEventId(e.target.value)}
          style={{ ...inputStyle, width: '300px' }}
        />
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
          style={inputStyle}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {error && (
        <div style={{ padding: '16px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : codes.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>No intent codes found.</div>
      ) : (
        <>
          <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
            {codes.length} code{codes.length !== 1 ? 's' : ''}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>QR</th>
                <th style={{ padding: '8px' }}>Code</th>
                <th style={{ padding: '8px', minWidth: '110px' }}>Type</th>
                <th style={{ padding: '8px' }}>Uses</th>
                <th style={{ padding: '8px' }}>Expires</th>
                <th style={{ padding: '8px' }}>Status</th>
                <th style={{ padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <CodeRow
                  key={c.id}
                  code={c}
                  onDeactivate={setConfirmDeactivate}
                  onToast={setToast}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="intent-codes"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null
      }}
    >
      {content}
    </DashboardLayout>
  );
}
